import { Link } from "react-router-dom";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { ArrowRight, ArrowUpRight, BedDouble, Bath, Ruler, Car, FileText } from "lucide-react";
import { IMAGES, API, resolveImage } from "../data/site";
import { useContent } from "../hooks/useCms";
import { FadeUp, Stagger, StaggerItem } from "../components/motion";
import CyclingImage from "../components/CyclingImage";
import PageHeader from "../components/PageHeader";
import EditorialMarquee from "../components/EditorialMarquee";
import SEO from "../components/SEO";

const TIERS = [
  {
    n: "01",
    title: "Custom Homes",
    text: "Thoughtfully designed homes shaped around your life, your lot, and your budget. Start from one of our plans or bring your own ideas — either way, every decision gets personal attention.",
    points: ["Your plan or ours", "Guided selections process", "Transparent pricing", "One point of contact"],
  },
];

const PlanCard = ({ plan }) => {
  const files = (plan.images?.length ? plan.images : plan.image ? [plan.image] : []).map(resolveImage);
  const [active, setActive] = useState(0);
  const current = files[active];
  const isPdf = current?.toLowerCase().split("?")[0].endsWith(".pdf");

  return (
    <div className="bg-white border border-[rgba(26,26,26,0.1)] group h-full flex flex-col" data-testid={`floor-plan-${plan.id}`}>
      {files.length > 0 && (
        <>
          <div className="aspect-[4/3] bg-white flex items-center justify-center p-6 border-b border-[rgba(26,26,26,0.08)]">
            {isPdf ? (
              <div className="flex flex-col items-center gap-3 text-[#5C5A56]">
                <FileText size={44} className="text-[#8C6A4F]" strokeWidth={1.2} />
                <span className="font-mono text-[0.62rem] tracking-[0.2em] uppercase font-bold">PDF Plan Sheet</span>
              </div>
            ) : (
              <img
                src={current}
                alt={`${plan.name} floor plan by On Point Homes`}
                className="max-w-full max-h-full object-contain transition-transform duration-700 group-hover:scale-[1.03]"
                loading="lazy"
              />
            )}
          </div>
          {files.length > 1 && (
            <div className="flex gap-2 px-4 pt-4">
              {files.map((f, i) => (
                <button
                  key={i}
                  onClick={() => setActive(i)}
                  data-testid={`plan-thumb-${plan.id}-${i}`}
                  aria-label={`Plan sheet ${i + 1}`}
                  className={`h-12 w-16 border flex items-center justify-center overflow-hidden transition-colors duration-300 ${i === active ? "border-[#8C6A4F]" : "border-[rgba(26,26,26,0.15)] hover:border-[#1A1A1A]"}`}
                >
                  {f.toLowerCase().split("?")[0].endsWith(".pdf")
                    ? <FileText size={16} className="text-[#8C6A4F]" />
                    : <img src={f} alt="" className="w-full h-full object-contain" loading="lazy" />}
                </button>
              ))}
            </div>
          )}
        </>
      )}
      <div className="p-6 flex flex-col grow">
        <div className="flex items-baseline justify-between gap-4">
          <h3 className="font-serif text-2xl tracking-tight">{plan.name}</h3>
          {plan.sqft && <span className="font-mono text-sm font-semibold whitespace-nowrap">{plan.sqft.toLocaleString()} SF</span>}
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1 text-xs font-mono tracking-wider text-[#5C5A56] uppercase">
          {plan.beds != null && <span className="flex items-center gap-1.5"><BedDouble size={14} className="text-[#8C6A4F]" /> {plan.beds} Bed</span>}
          {plan.baths != null && <span className="flex items-center gap-1.5"><Bath size={14} className="text-[#8C6A4F]" /> {plan.baths} Bath</span>}
          {plan.garage && <span className="flex items-center gap-1.5"><Car size={14} className="text-[#8C6A4F]" /> {plan.garage}</span>}
        </div>
        {plan.description && <p className="mt-4 text-sm text-[#5C5A56] leading-relaxed">{plan.description}</p>}
        <div className="mt-auto pt-6 flex gap-3">
          {current && (
            <a
              href={current}
              target="_blank"
              rel="noreferrer"
              data-testid={`view-plan-${plan.id}`}
              className="inline-flex items-center gap-2 bg-[#1A1A1A] text-[#F7F5F2] font-mono text-[0.62rem] tracking-[0.18em] uppercase font-bold px-5 py-3 btn-sweep"
            >
              View Plan <ArrowUpRight size={13} />
            </a>
          )}
          <Link
            to={`/contact?interest=${encodeURIComponent("Build a Custom Home")}`}
            data-testid={`plan-inquire-${plan.id}`}
            className="inline-flex items-center gap-2 border border-[#1A1A1A]/25 font-mono text-[0.62rem] tracking-[0.18em] uppercase font-bold px-5 py-3 hover:bg-[#1A1A1A] hover:text-[#F7F5F2] transition-colors duration-300"
          >
            Inquire
          </Link>
        </div>
      </div>
    </div>
  );
};

const FloorPlans = () => {
  const c = useContent();
  const { data: plans = [] } = useQuery({
    queryKey: ["floor-plans"],
    queryFn: async () => (await axios.get(`${API}/floor-plans`)).data,
  });

  return (
    <section data-testid="floor-plans-section" className="py-24 md:py-36 bg-[#EFECE7]">
      <div className="max-w-[1600px] mx-auto px-5 md:px-10">
        <FadeUp className="max-w-3xl">
          <p className="eyebrow text-[#8C6A4F] mb-6">Floor Plans</p>
          <h2 className="font-serif text-4xl md:text-6xl leading-[1.02] tracking-tight">
            {c("custom_plans_headline1", "Start With a Plan.")} <span className="italic text-[#8C6A4F]">{c("custom_plans_headline2", "Make It Yours.")}</span>
          </h2>
          <p className="mt-8 text-base md:text-lg leading-relaxed text-[#5C5A56]">
            {c("custom_plans_intro", "Not every home needs to start from scratch. Explore floor plans we've designed and built before, then make them your own.")}
          </p>
        </FadeUp>

        <Stagger className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-px bg-[rgba(26,26,26,0.1)]">
          {[
            { title: c("custom_existing_title", "Existing Floor Plans"), text: c("custom_existing_text", "") },
            { title: c("custom_full_title", "Fully Custom Plans"), text: c("custom_full_text", "") },
          ].map((o) => (
            <StaggerItem key={o.title} className="bg-[#F7F5F2] p-8 md:p-12">
              <h3 className="font-serif text-2xl md:text-3xl tracking-tight">{o.title}</h3>
              <p className="mt-4 text-[#5C5A56] leading-relaxed text-sm md:text-base">{o.text}</p>
            </StaggerItem>
          ))}
        </Stagger>

        {plans.length > 0 ? (
          <Stagger className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="floor-plans-grid">
            {plans.map((plan) => (
              <StaggerItem key={plan.id}>
                <PlanCard plan={plan} />
              </StaggerItem>
            ))}
          </Stagger>
        ) : (
          <FadeUp className="mt-16 border-t border-[rgba(26,26,26,0.12)] pt-12" delay={0.1}>
            <p className="font-serif italic text-2xl md:text-3xl text-[#A3A09B] leading-snug" data-testid="floor-plans-empty">
              Our plan gallery is being assembled — in the meantime, just ask. We'll walk you through
              the plans we've already designed and built.
            </p>
          </FadeUp>
        )}

        <FadeUp className="mt-20 text-center" delay={0.1}>
          <h3 className="font-serif text-3xl md:text-5xl tracking-tight">{c("custom_cta_headline", "Find a Plan That Fits You")}</h3>
          <p className="mt-5 max-w-xl mx-auto text-[#5C5A56] leading-relaxed">
            {c("custom_cta_text", "Whether you want to modify one of our existing plans or create something completely new, let's design a home that works for you.")}
          </p>
          <Link
            to="/contact?interest=Build%20a%20Custom%20Home"
            data-testid="floor-plans-cta-btn"
            className="mt-8 inline-flex items-center gap-3 bg-[#1A1A1A] text-[#F7F5F2] font-mono text-[0.7rem] tracking-[0.2em] uppercase font-bold px-10 py-5 btn-sweep"
          >
            {c("custom_cta_button", "Start Your Home")} <ArrowRight size={14} />
          </Link>
        </FadeUp>
      </div>
    </section>
  );
};

const PROMISES = [
  { title: "Personal Attention", text: "You build with the builder. Direct access, quick answers, and someone on your job site who knows your name." },
  { title: "Thoughtful Design", text: "Every square foot earns its place. We design for how you actually live — light, flow, storage, and the moments in between." },
  { title: "Craftsmanship", text: "Straight lines, tight reveals, honest materials. We hold our trade partners to the standard we'd want in our own home." },
  { title: "Communication", text: "Regular updates, scheduled walkthroughs, and no surprises. You'll always know where your build stands." },
];

const CustomHomes = () => {
  const c = useContent();
  return (
  <main>
    <SEO />
    <PageHeader
      testid="custom-homes-header"
      eyebrow="Idaho Custom Home Builder"
      lines={[{ text: c("custom_headline1", "Custom Homes,") }, { text: c("custom_headline2", "Built For You"), italic: true }]}
      description={c("custom_intro", "Thoughtfully designed custom homes built around the way you live — a personal, hands-on building experience from first sketch to final walkthrough.")}
    />

    <section className="pb-24 md:pb-36">
      <div className="max-w-[1600px] mx-auto px-5 md:px-10 grid grid-cols-12 gap-4 md:gap-6">
        <FadeUp className="col-span-12 md:col-span-8 img-zoom relative aspect-[16/9]">
          <CyclingImage start={5} interval={5400} alt="Custom home interior by On Point Homes, Idaho custom home builder" />
        </FadeUp>
        <FadeUp className="col-span-12 md:col-span-4 img-zoom relative aspect-[16/9] md:aspect-auto" delay={0.15}>
          <CyclingImage start={11} interval={5800} alt="Custom home built by On Point Homes in Idaho" />
        </FadeUp>
      </div>
    </section>

    <section className="pb-24 md:pb-36">
      <div className="max-w-[1600px] mx-auto px-5 md:px-10">
        <Stagger className="grid grid-cols-1 max-w-3xl gap-px bg-[rgba(26,26,26,0.1)]">
          {TIERS.map((t) => (
            <StaggerItem key={t.n} className="bg-[#F7F5F2] p-8 md:p-14">
              <h2 className="font-serif text-3xl md:text-5xl tracking-tight">{t.title}</h2>
              <p className="mt-5 text-[#5C5A56] leading-relaxed text-base md:text-lg">{t.text}</p>
              <ul className="mt-8 flex flex-col gap-3">
                {t.points.map((p, i) => (
                  <li key={i} className="flex items-center gap-3 font-mono text-[0.7rem] tracking-[0.15em] uppercase font-semibold text-[#1A1A1A] border-b border-[rgba(26,26,26,0.08)] pb-3">
                    <span className="w-1.5 h-1.5 rotate-45 bg-[#8C6A4F] shrink-0" /> {p}
                  </li>
                ))}
              </ul>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>

    <FloorPlans />

    <EditorialMarquee dark items={["Personal Attention", "Thoughtful Design", "Craftsmanship", "Communication", "Hands-On Experience"]} />

    <section className="py-24 md:py-36 bg-[#1A1A1A] text-[#F7F5F2]">
      <div className="max-w-[1600px] mx-auto px-5 md:px-10">
        <FadeUp className="mb-14 md:mb-20">
          <p className="eyebrow text-[#8C6A4F] mb-6">What You Can Expect</p>
          <h2 className="font-serif text-4xl md:text-6xl tracking-tight leading-[1.02] max-w-3xl">
            A hands-on experience, start to finish.
          </h2>
        </FadeUp>
        <Stagger className="grid grid-cols-1 md:grid-cols-2 gap-x-16">
          {PROMISES.map((p, i) => (
            <StaggerItem key={p.title}>
              <div className="py-10 border-b border-white/10 grid grid-cols-12 gap-4">
                <span className="col-span-2 font-serif text-4xl text-[#8C6A4F]">0{i + 1}</span>
                <div className="col-span-10">
                  <h3 className="font-serif text-2xl md:text-3xl tracking-tight">{p.title}</h3>
                  <p className="mt-3 text-[#F7F5F2]/65 leading-relaxed text-sm md:text-base">{p.text}</p>
                </div>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
        <FadeUp delay={0.2} className="mt-16">
          <Link to="/contact?interest=Build a Custom Home" data-testid="custom-cta-btn" className="btn-sweep inline-flex items-center gap-3 bg-[#F7F5F2] text-[#1A1A1A] font-mono text-[0.7rem] tracking-[0.2em] uppercase font-bold px-10 py-5 hover:text-[#F7F5F2] transition-colors duration-300">
            Start Your Custom Home <ArrowRight size={14} />
          </Link>
        </FadeUp>
      </div>
    </section>
  </main>
  );
};

export default CustomHomes;
